import React, { useState } from 'react'
import { Box, Typography } from '@mui/material'
import CellEditComponent from './CellEditComponent'

import { ReactComponent as ArrowIcon } from '../../../../../assets/images/Arrow_Down_Icon.svg'
import { ReactComponent as PlusIcon } from '../../../../../assets/images/add-circle.svg'

import type { TPPSection } from '../queries/useGetTPPSectionsByTPPIdQuery'
import type { TPPRow } from '../queries/useGetTPPRowsByTPPIdQuery'
import type { TPPCell } from '../../TPPCell/queries/useTPPCellQuery'

export interface SectionEditComponentProps {
    section: TPPSection
    rows: TPPRow[]
    cells: TPPCell[]
    onCellChange?: (cellId: string, value: string) => void
    onCellDelete?: (cellId: string) => void
}

const SectionEditComponent: React.FC<SectionEditComponentProps> = ({
    section,
    rows,
    cells,
    onCellChange,
    onCellDelete,
}) => {
    const [isExpanded, setIsExpanded] = useState(true)

    const handleToggle = () => {
        setIsExpanded((prev) => !prev)
    }

    const [extraCellsByRow, setExtraCellsByRow] = useState<Record<string, { id: string }[]>>({})

    const [deletedExistingCellIds, setDeletedExistingCellIds] = useState<Record<string, boolean>>({})

    const handleAddCellForRow = (rowId: string) => {
        setIsExpanded(true)
        setExtraCellsByRow((prev) => {
            const existing = prev[rowId] ?? []
            const newId = `new-${rowId}-${existing.length + 1}-${Date.now()}`
            return {
                ...prev,
                [rowId]: [...existing, { id: newId }],
            }
        })
    }

    const handleDeleteCell = (rowId: string, cellId: string) => {
        // First, check if this is a newly added (extra) cell for this row and remove it
        setExtraCellsByRow((prev) => {
            const extrasForRow = prev[rowId] ?? []
            const hasExtra = extrasForRow.some((e) => e.id === cellId)
            if (!hasExtra) {
                return prev
            }
            return {
                ...prev,
                [rowId]: extrasForRow.filter((e) => e.id !== cellId),
            }
        })

        // For existing cells, mark them as deleted so we filter them out of the ordered list
        setDeletedExistingCellIds((prev) => {
            if (cells.some((c) => c._id === cellId)) {
                return {
                    ...prev,
                    [cellId]: true,
                }
            }
            return prev
        })

        if (onCellDelete) {
            onCellDelete(cellId)
        }
    }

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignSelf: 'stretch',
                gap: 1.5,
            }}
        >
            {/* Header row with title and toggle button */}
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    alignSelf: 'stretch',
                }}
            >
                <Typography
                    variant="20x500"
                    sx={{
                        color: 'figma.grayscale.textHier1',
                        fontFamily: '"DM Sans", sans-serif',
                        fontSize: 20,
                        fontStyle: 'normal',
                        fontWeight: 500,
                        lineHeight: '150%', // 30px
                    }}
                >
                    {section.title}
                </Typography>
                <Box
                    component="button"
                    type="button"
                    onClick={handleToggle}
                    sx={{
                        width: 24,
                        height: 24,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: 'none',
                        padding: 0,
                        margin: 0,
                        background: 'transparent',
                        cursor: 'pointer',
                        color: 'figma.grayscale.tier1',
                    }}
                >
                    <ArrowIcon
                        style={{
                            width: 24,
                            height: 24,
                            transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                            transition: 'transform 0.15s ease-out',
                        }}
                    />
                </Box>
            </Box>

            {/* Rows list, visible only when expanded */}
            {isExpanded && rows && rows.length > 0 && (
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1,
                    }}
                >
                    {rows.map((row, rowIndex) => {
                        const rowSlots = (row as any).slotIds ?? []

                        const existingOrderedCellsForRow = Array.isArray(rowSlots)
                            ? rowSlots
                                  // Step 1: map each slotId to the corresponding cell
                                  .map((slot: any) => {
                                      const slotId = typeof slot === 'string' ? slot : slot._id
                                      const cell = cells.find((c) => c.slotId === slotId)
                                      return cell ?? null
                                  })
                                  // Step 2: drop slots with no cell or marked as deleted
                                  .filter((cell): cell is TPPCell => !!cell && !deletedExistingCellIds[cell._id])
                                  // Step 3: assign sequential order 1..n for existing cells
                                  .map((cell, index) => ({
                                      cell,
                                      order: index + 1,
                                  }))
                            : []

                        const extraCellsForRow = extraCellsByRow[row._id] ?? []

                        return (
                            <Box
                                key={row._id}
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignSelf: 'stretch',
                                    gap: 1,
                                }}
                            >
                                {/* Row header with title and plus icon */}
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'flex-start',
                                        alignItems: 'center',
                                        gap: '12px',
                                        alignSelf: 'stretch',
                                    }}
                                >
                                    <Typography
                                        variant="16x500"
                                        sx={{
                                            color: 'figma.grayscale.textHier2',
                                            fontFamily: '"DM Sans", sans-serif',
                                            fontSize: 16,
                                            fontStyle: 'normal',
                                            fontWeight: 500,
                                            lineHeight: '150%', // 24px
                                        }}
                                    >
                                        {row.title}
                                    </Typography>

                                    <Box
                                        component="button"
                                        type="button"
                                        onClick={() => handleAddCellForRow(row._id)}
                                        sx={{
                                            width: 24,
                                            height: 24,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            border: 'none',
                                            padding: 0,
                                            margin: 0,
                                            background: 'transparent',
                                            color: 'figma.primary',
                                            cursor: 'pointer',
                                        }}
                                    >
                                        <PlusIcon
                                            style={{
                                                width: 24,
                                                height: 24,
                                            }}
                                        />
                                    </Box>
                                </Box>

                                {/* Existing cells for this row, in slot order, with numbered labels based on slotIds */}
                                {existingOrderedCellsForRow.map(({ cell, order }) => (
                                    <CellEditComponent
                                        key={cell._id}
                                        cell={cell}
                                        order={order}
                                        onChange={onCellChange}
                                        onDelete={(cellId) => handleDeleteCell(row._id, cellId)}
                                    />
                                ))}

                                {/* Newly added blank cells, appended after existing ones */}
                                {extraCellsForRow.map((extraCell, index) => {
                                    const order = existingOrderedCellsForRow.length + index + 1
                                    const blankCell = {
                                        _id: extraCell.id,
                                        tppId: '',
                                        columnId: '',
                                        slotId: '',
                                        content: '',
                                        linkedEvidence: [],
                                        linkedActivities: [],
                                        linkedValueMessages: [],
                                        linkedKeyEvidenceStatement: [],
                                        isHighlighted: false,
                                        locked: null,
                                        isClone: false,
                                        cloneSource: null,
                                        isSnapshot: false,
                                    } as TPPCell

                                    return (
                                        <CellEditComponent
                                            key={blankCell._id}
                                            cell={blankCell}
                                            order={order}
                                            onChange={onCellChange}
                                            onDelete={(cellId) => handleDeleteCell(row._id, cellId)}
                                        />
                                    )
                                })}
                            </Box>
                        )
                    })}
                </Box>
            )}
        </Box>
    )
}

export default SectionEditComponent
